/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Build 91 P1-9 — React hook that subscribes to the new
// /users/{uid}/safeCircle collection and exposes
// add / remove handlers. Pure binding layer over staticMembers.ts —
// the helper module stays framework-agnostic so it can be unit-
// tested without renderer scaffolding.
//
// Build 93 — return shape upgraded to CircleMemberView[] so the orbit
// view + member detail screen get a single typed model. MockFriend is
// prepended client-side when the store flag is unset; its removal goes
// through dismissMockFriend (the persisted zustand bit), NOT through
// the RTDB delete path that real members use.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getDatabase, onValue, ref } from "firebase/database";
import { useAuth } from "@/contexts/AuthContext";
import { useSafeCircleStore } from "@/stores/safeCircleStore";
import {
  addSafeCircleMember,
  parseSafeCircleMember,
  removeSafeCircleMember,
  sortSafeCircleMembers,
  type SafeCircleStaticMember,
} from "@/utils/safecircle/staticMembers";
import {
  buildCircleView,
  MOCK_FRIEND_ID,
  type CircleMemberView,
} from "@/utils/safecircle/mockFriend";
import { isMockDismissedForUid } from "@/utils/safecircle/mockFriendDismissal";
import { useUserPresenceSnapshot } from "./useUserPresenceSnapshot";

interface AddMemberInput {
  displayName: string;
  phoneNumber: string;
  linkedFriendUid?: string;
  relation?: string;
}

export interface UseSafeCircleStaticMembersResult {
  /** Full view list — MockFriend (when not dismissed) + persisted
   *  members, both shaped as CircleMemberView. */
  members: CircleMemberView[];
  /** Persisted (real) members only. Drives cap math and the
   *  MockFriend remove-rule. */
  rawMembers: SafeCircleStaticMember[];
  /** Convenience: rawMembers.length. */
  realMemberCount: number;
  /** True if MockFriend is currently in the `members` list. */
  mockFriendVisible: boolean;
  /** Logged-in user's current city, for the "You" centre node. */
  userCity?: string;
  loading: boolean;
  /** Resolves with the newly-stored member, or null when the cap was
   *  hit / no signed-in user. Errors propagate so callers can toast. */
  addMember: (input: AddMemberInput) => Promise<SafeCircleStaticMember | null>;
  /** Routes MockFriend's id to the local-dismiss path, everything else
   *  to the RTDB delete. */
  removeMember: (memberId: string) => Promise<void>;
}

export function useSafeCircleStaticMembers(): UseSafeCircleStaticMembersResult {
  const { user } = useAuth();
  const dismissedMockForUids = useSafeCircleStore((s) => s.dismissedMockForUids);
  const dismissMockForUid = useSafeCircleStore((s) => s.dismissMockForUid);
  // Per-account: MockFriend is dismissed only for the uid that dismissed it,
  // so a new account on a previously-used device still gets the example.
  const mockFriendDismissed = isMockDismissedForUid(
    dismissedMockForUids,
    user?.uid,
  );
  const [rawMembers, setRawMembers] = useState<SafeCircleStaticMember[]>([]);
  const [loading, setLoading] = useState(true);
  // Build 93 — MockFriend mirrors the logged-in user's live presence:
  // status, city, batteryPct, isCharging, lastUpdatedAt all come from
  // useUserPresenceSnapshot rather than hardcoded sample values. Real
  // (non-mock) members are unaffected by this overlay.
  const userPresence = useUserPresenceSnapshot();

  useEffect(() => {
    if (!user?.uid) {
      setRawMembers([]);
      setLoading(false);
      return;
    }
    const database = getDatabase();
    const collectionRef = ref(database, `users/${user.uid}/safeCircle`);
    const unsub = onValue(
      collectionRef,
      (snap) => {
        if (!snap.exists()) {
          setRawMembers([]);
          setLoading(false);
          return;
        }
        const raw = snap.val() as Record<string, unknown>;
        const parsed: SafeCircleStaticMember[] = [];
        for (const [memberId, value] of Object.entries(raw)) {
          const m = parseSafeCircleMember(memberId, value);
          if (m) parsed.push(m);
        }
        setRawMembers(sortSafeCircleMembers(parsed));
        setLoading(false);
      },
      (err) => {
        console.error("[useSafeCircleStaticMembers] subscribe failed:", err);
        setRawMembers([]);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [user]);

  const addMember = useCallback(
    async (input: AddMemberInput) => {
      if (!user?.uid) return null;
      const database = getDatabase();
      return addSafeCircleMember(database, user.uid, input, rawMembers.length);
    },
    [user, rawMembers.length],
  );

  const removeMember = useCallback(
    async (memberId: string) => {
      if (memberId === MOCK_FRIEND_ID) {
        // MockFriend never touched Firebase — record the dismissal for THIS
        // uid only (per-account) so the next map drops it for this user
        // without suppressing it for other accounts on the same device.
        dismissMockForUid(user?.uid);
        return;
      }
      if (!user?.uid) return;
      const database = getDatabase();
      // Pass the current member list so the SafeCircle reader-index entry is
      // cleared iff no other member still links the same friend uid.
      await removeSafeCircleMember(database, user.uid, memberId, rawMembers);
    },
    [user, rawMembers, dismissMockForUid],
  );

  const members = useMemo(
    () =>
      buildCircleView(rawMembers, mockFriendDismissed, Date.now(), userPresence),
    [rawMembers, mockFriendDismissed, userPresence],
  );

  return useMemo(
    () => ({
      members,
      rawMembers,
      realMemberCount: rawMembers.length,
      mockFriendVisible: !mockFriendDismissed,
      userCity: userPresence.city,
      loading,
      addMember,
      removeMember,
    }),
    [
      members,
      rawMembers,
      mockFriendDismissed,
      userPresence.city,
      loading,
      addMember,
      removeMember,
    ],
  );
}
